/**
 * @fileoverview Civil & Criminal Offences — all-findings cross-head table
 * @module jobs/phases/review/civilCriminalOffences/OffenceAllFindingsTable
 *
 * Every active finding across all twelve heads, with search, decision filter,
 * and a grouping toggle (None / Head / Respondent) — the offence equivalent of
 * the flagged "All flags" view. Rows open the same tabbed review modal.
 */

import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { AlertTriangle, Search } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { StatuteBadge } from "../flaggedTransactions/AgentText";
import { OffenceFindingModal } from "./OffenceFindingModal";
import {
  fmtMoney,
  fmtCapacity,
  friendlySource,
  humaniseTokens,
  BANDING_BADGE,
  OFFENCE_DECISION_BADGE,
  type OffenceFindingUI,
  type OffenceHeadUI,
} from "./offenceShared";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

type GroupBy = "none" | "head" | "respondent";

export function OffenceAllFindingsTable(props: {
  jobId: Id<"jobs">;
  heads: OffenceHeadUI[];
  isViewer: boolean;
}) {
  const { jobId, heads, isViewer } = props;
  const [search, setSearch] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [groupBy, setGroupBy] = useState<GroupBy>("head");
  const [openId, setOpenId] = useState<string | null>(null);

  const headByKey = useMemo(
    () => new Map(heads.map((h) => [h.headKey, h])),
    [heads],
  );

  const { data: findings } = useQuery(
    convexQuery(offences.publicQueries.listFindings, { jobId }),
  );
  const rows: OffenceFindingUI[] = useMemo(() => findings ?? [], [findings]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter(
      (f) =>
        (decisionFilter === "all" || f.decision === decisionFilter) &&
        (!s ||
          f.respondentName.toLowerCase().includes(s) ||
          f.conductSummary.toLowerCase().includes(s)),
    );
  }, [rows, search, decisionFilter]);

  const groups = useMemo(() => {
    if (groupBy === "none")
      return [["", filtered]] as [string, OffenceFindingUI[]][];
    const map = new Map<string, OffenceFindingUI[]>();
    for (const f of filtered) {
      const key =
        groupBy === "head"
          ? (headByKey.get(f.headKey)?.name ?? friendlySource(f.headKey))
          : f.respondentName;
      const list = map.get(key) ?? [];
      list.push(f);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [filtered, groupBy, headByKey]);

  const orderedIds = filtered.map((f) => f._id);
  const openFinding = openId ? rows.find((f) => f._id === openId) : null;
  const openHead = openFinding ? headByKey.get(openFinding.headKey) : undefined;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 border-b p-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search respondent or conduct…"
            className="h-8 pl-8 text-xs"
          />
        </div>
        <Select value={decisionFilter} onValueChange={setDecisionFilter}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All decisions</SelectItem>
            {Object.entries(OFFENCE_DECISION_BADGE).map(([value, b]) => (
              <SelectItem key={value} value={value}>
                {b.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No grouping</SelectItem>
            <SelectItem value="head">Group by head</SelectItem>
            <SelectItem value="respondent">Group by respondent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Respondent</TableHead>
            <TableHead>Head</TableHead>
            <TableHead className="w-[34%]">Conduct</TableHead>
            <TableHead className="text-right">Quantum</TableHead>
            <TableHead>Decision</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="py-8 text-center text-sm text-muted-foreground"
              >
                No findings match.
              </TableCell>
            </TableRow>
          )}
          {groups.map(([groupLabel, list]) => (
            <Fragment key={groupLabel || "all"}>
              {groupBy !== "none" && (
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell colSpan={5} className="py-1.5">
                    <span className="text-xs font-semibold">{groupLabel}</span>
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {list.length} finding{list.length === 1 ? "" : "s"}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {list.map((f) => {
                const head = headByKey.get(f.headKey);
                const decision = OFFENCE_DECISION_BADGE[f.decision];
                const banding = BANDING_BADGE[f.banding];
                return (
                  <TableRow
                    key={f._id}
                    className="cursor-pointer"
                    onClick={() => setOpenId(f._id)}
                  >
                    <TableCell>
                      <div className="text-sm font-medium">
                        {f.respondentName}
                      </div>
                      {f.respondentCapacity && (
                        <div className="text-[11px] text-muted-foreground">
                          {fmtCapacity(f.respondentCapacity)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="text-xs">
                          {head?.name ?? friendlySource(f.headKey)}
                        </span>
                        <StatuteBadge>{f.provision}</StatuteBadge>
                        {f.criminalOverlay?.engaged && (
                          <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {humaniseTokens(f.conductSummary)}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      {typeof f.quantum === "number" ? (
                        <span className="font-mono text-sm tabular-nums">
                          {fmtMoney(f.quantum)}
                        </span>
                      ) : (
                        <Badge color={banding.color} variant="soft" size="sm">
                          {banding.label}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        color={decision.color}
                        variant="soft"
                        size="sm"
                        className="whitespace-nowrap"
                      >
                        {decision.label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>

      {openId && openFinding && openHead && (
        <OffenceFindingModal
          jobId={jobId}
          head={openHead}
          findingId={openId as Id<"offenceFindings">}
          orderedIds={orderedIds}
          isViewer={isViewer}
          onClose={() => setOpenId(null)}
          onNavigate={(id) => setOpenId(id)}
        />
      )}
    </div>
  );
}
